import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import {
  assignClassToCategory,
  moveClassToCategory,
  sortClassInCategory,
} from '@/features/class/boardsApi';
import type { ClassAssignment } from '@/features/profile/classes/classCategoryAssignmentUtils';
import { UNCATEGORISED_CATEGORY_ID } from '@/features/profile/classes/classCategoryAssignmentUtils';
import { useAuthStore } from '@/store/authStore';

interface ExistingMapping {
  boardCategoryId: number;
  categoryId: number;
}

export type ClassAssignmentWriteKind = 'create' | 'move' | 'sort';

/** Choose the endpoint operation without treating the `-1` unassignment as a new mapping. */
export function resolveClassAssignmentWrite(
  existingCategoryId: number | null,
  targetCategoryId: number,
): ClassAssignmentWriteKind {
  if (existingCategoryId == null) return 'create';
  return existingCategoryId === targetCategoryId ? 'sort' : 'move';
}

function readMappingId(board: Record<string, unknown>): number {
  const nested = [board.boardCategory, board.categoryMapping, board.boardCategoryMapping].find(
    (value) => value && typeof value === 'object',
  ) as Record<string, unknown> | undefined;
  const candidates = [
    board.boardCategoryId,
    board.boardCategoryMappingId,
    board.boardCategoryMappingID,
    board.boardCategoryMapId,
    board.boardCategory,
    board.classCategoryId,
    board.classCategoryMappingId,
    board.categoryMappingId,
    board.categoryClassMappingId,
    board.mappingId,
    nested?.boardCategoryId,
    nested?.mappingId,
    nested?.id,
  ];
  for (const candidate of candidates) {
    if (typeof candidate !== 'number' && typeof candidate !== 'string') continue;
    const id = Number(candidate);
    if (Number.isFinite(id)) return id;
  }
  return Number.NaN;
}

function getExistingMappings(): Map<number, ExistingMapping> {
  const mappings = new Map<number, ExistingMapping>();
  const groups = useAuthStore.getState().rawUser?.boardWithCategory;
  if (!Array.isArray(groups)) return mappings;
  for (const group of groups) {
    const categoryId = Number((group as { categoryId?: unknown }).categoryId);
    const boards = (group as { boards?: unknown }).boards;
    if (!Array.isArray(boards)) continue;
    for (const row of boards) {
      if (!row || typeof row !== 'object') continue;
      const board = row as Record<string, unknown>;
      const boardId = Number(board.id ?? board.boardId);
      const boardCategoryId = readMappingId(board);
      if (Number.isFinite(boardId) && Number.isFinite(boardCategoryId)) {
        mappings.set(boardId, { boardCategoryId, categoryId });
      }
    }
  }
  return mappings;
}

function findBoardCategoryId(boardId: number): number {
  const groups = useAuthStore.getState().rawUser?.boardWithCategory;
  if (!Array.isArray(groups)) return UNCATEGORISED_CATEGORY_ID;
  for (const group of groups) {
    const categoryId = Number((group as { categoryId?: unknown }).categoryId);
    const boards = (group as { boards?: unknown }).boards;
    if (!Array.isArray(boards)) continue;
    const containsBoard = boards.some((row) => {
      if (!row || typeof row !== 'object') return false;
      const board = row as Record<string, unknown>;
      return Number(board.id ?? board.boardId) === boardId;
    });
    if (containsBoard) {
      return Number.isFinite(categoryId) ? categoryId : UNCATEGORISED_CATEGORY_ID;
    }
  }
  return UNCATEGORISED_CATEGORY_ID;
}

function persistMappingIds(changed: Map<number, ExistingMapping>): void {
  if (changed.size === 0) return;
  const state = useAuthStore.getState();
  const rawUser = state.rawUser;
  if (!rawUser || !Array.isArray(rawUser.boardWithCategory)) return;
  state.setRawUser({
    ...rawUser,
    boardWithCategory: rawUser.boardWithCategory.map((group) => {
      if (!group || typeof group !== 'object') return group;
      const record = group as Record<string, unknown>;
      if (!Array.isArray(record.boards)) return group;
      return {
        ...record,
        boards: record.boards.map((row) => {
          if (!row || typeof row !== 'object') return row;
          const board = row as Record<string, unknown>;
          const mapping = changed.get(Number(board.id ?? board.boardId));
          return mapping ? { ...board, boardCategoryId: mapping.boardCategoryId } : row;
        }),
      };
    }),
  });
}

/**
 * Persist a batch of board→category assignments via repeated
 * `POST /midl/api/v3/class/category/addclass`.
 *
 * Calls run sequentially so the first failure aborts the batch — the caller
 * then skips rewriting `rawUser` to avoid drift between the UI and the
 * backend. On both success and failure the category list is invalidated so
 * any already-applied writes are reconciled on the next fetch.
 */
export function useSaveCategoryAssignmentsMutation() {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, readonly ClassAssignment[]>({
    mutationKey: ['class', 'category', 'assign'],
    mutationFn: async (assignments) => {
      const existingMappings = getExistingMappings();
      const changedMappings = new Map<number, ExistingMapping>();
      for (const assignment of assignments) {
        const existing = existingMappings.get(assignment.boardId);
        const writeKind = resolveClassAssignmentWrite(
          existing?.categoryId ?? null,
          assignment.categoryId,
        );
        if (existing && writeKind !== 'create') {
          if (writeKind === 'sort') {
            await sortClassInCategory({
              boardCategoryId: existing.boardCategoryId,
              newPosition: assignment.position,
            });
          } else {
            const moved = await moveClassToCategory({
              boardCategoryId: existing.boardCategoryId,
              boardId: assignment.boardId,
              categoryId: assignment.categoryId,
              position: assignment.position,
            });
            if (moved) {
              const mapping = {
                boardCategoryId: moved.boardCategoryId,
                categoryId: moved.categoryId,
              };
              existingMappings.set(assignment.boardId, mapping);
              changedMappings.set(assignment.boardId, mapping);
            }
          }
          continue;
        }
        const created = await assignClassToCategory({
          boardId: assignment.boardId,
          categoryId: assignment.categoryId,
          position: assignment.position,
        });
        if (created) {
          const mapping = {
            boardCategoryId: created.boardCategoryId,
            categoryId: created.categoryId,
          };
          existingMappings.set(assignment.boardId, mapping);
          changedMappings.set(assignment.boardId, mapping);
        }
      }
      persistMappingIds(changedMappings);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.class.categories });
      void queryClient.invalidateQueries({ queryKey: ['boards', 'joined'] });
    },
    onError: () => {
      // Some assignments may have committed before the failure — refetch so
      // the popup reflects the real backend state.
      void queryClient.invalidateQueries({ queryKey: queryKeys.class.categories });
    },
  });
}

export interface SortClassByBoardIdRequest {
  readonly boardId: number | string;
  readonly newPosition: number;
}

/** Resolve a board's mapping id from login state and sort it immediately. */
export function useSortClassInCategoryMutation() {
  const queryClient = useQueryClient();
  return useMutation<number, unknown, SortClassByBoardIdRequest>({
    mutationKey: ['class', 'category', 'class-sort'],
    mutationFn: async ({ boardId, newPosition }) => {
      const numericBoardId = Number(boardId);
      const mapping = getExistingMappings().get(numericBoardId);
      await sortClassInCategory({
        // Named categories expose their mapping id in login data. The
        // uncategorised bucket does not, so its class/board id is the only id
        // available for the controller's /category/class/sort/{id} route.
        boardCategoryId: mapping?.boardCategoryId ?? numericBoardId,
        newPosition,
      });
      return mapping?.categoryId ?? findBoardCategoryId(numericBoardId);
    },
    onSuccess: (categoryId, { boardId, newPosition }) => {
      const state = useAuthStore.getState();
      const rawUser = state.rawUser;
      if (!rawUser || !Array.isArray(rawUser.boardWithCategory)) return;
      state.setRawUser({
        ...rawUser,
        boardWithCategory: rawUser.boardWithCategory.map((group) => {
          if (!group || typeof group !== 'object') return group;
          const record = group as Record<string, unknown>;
          if (Number(record.categoryId) !== categoryId || !Array.isArray(record.boards)) {
            return group;
          }
          const boards = [...record.boards];
          const currentIndex = boards.findIndex((row) => {
            if (!row || typeof row !== 'object') return false;
            const board = row as Record<string, unknown>;
            return Number(board.id ?? board.boardId) === Number(boardId);
          });
          if (currentIndex < 0) return group;
          const [moved] = boards.splice(currentIndex, 1);
          boards.splice(Math.min(newPosition - 1, boards.length), 0, moved);
          return {
            ...record,
            boards: boards.map((row, index) =>
              row && typeof row === 'object'
                ? { ...(row as Record<string, unknown>), position: index + 1 }
                : row,
            ),
          };
        }),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['boards', 'joined'] });
    },
  });
}
